import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { readFlow } from '@api/fast_tracks';

import {
  createErrorData,
  fastTrackErrorStatuses,
  fastTrackFlowPendingStatuses,
  fastTrackStatus,
  flowTag,
} from '@helpers';
import { Error, LoaderView, PageTitle } from '@utilities';

import Column from '../../layouts/Column';
import FastTrackTitle from './components/FastTrackTitle';
import FastTrackLoadingDots from './components/FastTrackLoadingDots';
import FastTrackTopicCard from './components/FastTrackTopicCard';
import ModalBuildFastTrack from '../FastTracks/components/ModalBuildFastTrack';

import { loaderHeader, loaderMessage } from './utilities/helpers';

const FastTrack = () => {
  const { flow_id: flowId } = useParams();
  const [error, setError] = useState(null);
  const [flow, setFlow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLoadingDots, setShowLoadingDots] = useState(true);
  const [downloadData, setDownloadData] = useState(new Map());

  const fastTrackFlow = useSelector((state) =>
    state?.websocket?.flows?.find((flow) => Number(flow?.id) === Number(flowId))
  );

  const fastTrackRoute = useMemo(() => {
    if (!flow) return {};
    return {
      id: flow?.fast_track_id,
      name: flow?.fastTrackName,
      path: `/dashboard/narrative/${flow?.fast_track_id}`,
    };
  }, [flow]);

  const fetchFlow = async () => {
    try {
      const response = await readFlow({ id: flowId });
      if (fastTrackErrorStatuses.includes(response?.status)) {
        setError(createErrorData({ status: 500 }));
        setIsLoading(false);
      }
      if (response?.status === fastTrackStatus.COMPLETED && flowTag.NO_SAMPLE in response?.tags) {
        setError(
          createErrorData({
            description: response?.tags[flowTag.NO_SAMPLE],
            header: 'Insufficient Sample',
            status: 'failed-flow',
          })
        );
      }
      setFlow(response);
      if (!fastTrackFlowPendingStatuses.includes(response?.status)) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setError(createErrorData({ status: error?.status }));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Handle Reprompt
    if (!isLoading) {
      setIsLoading(true);
    }

    setError(null);
    fetchFlow();
  }, [flowId]);

  useEffect(() => {
    const topics = flow?.topics || [];
    if (!topics) return;

    const shouldShowLoadingDots = topics.some((topic) => {
      return [
        fastTrackStatus.QUEUED,
        fastTrackStatus.IN_PROGRESS,
        fastTrackStatus.PENDING,
      ].includes(topic.status);
    });
    setShowLoadingDots(shouldShowLoadingDots);
  }, [flow]);

  useEffect(() => {
    if (!fastTrackFlow) return;

    const data = fastTrackFlow;

    if (fastTrackFlowPendingStatuses.includes(data?.status)) {
      setFlow((prevFlow) => ({
        ...prevFlow,
        status: data?.status,
      }));
      return;
    }

    if (!flow?.topics?.length) {
      // If the Websocket sends updated flow before first fetch, need to fetch the flow to update the state with created flow's topics
      fetchFlow();
      return;
    }

    const updateCompletedTopics = () => {
      const completeFlowTopicIds = new Set(data.completeFlowTopicIds);

      setFlow((prevFlow) => ({
        ...prevFlow,
        topics: prevFlow.topics?.map((topic) => {
          if (completeFlowTopicIds.has(topic.id) && topic.status !== fastTrackStatus.COMPLETED) {
            return { ...topic, status: fastTrackStatus.COMPLETED };
          }
          return topic;
        }),
      }));
    };

    const updateIncompleteTopics = () => {
      const incompleteFlowTopicIds = new Set(data?.incompleteFlowTopicIds);

      setFlow((prevFlow) => ({
        ...prevFlow,
        topics: prevFlow.topics?.map((topic) => {
          if (incompleteFlowTopicIds.has(topic.id) && topic.status !== fastTrackStatus.EXCEPTION) {
            return { ...topic, status: fastTrackStatus.EXCEPTION };
          }
          return topic;
        }),
      }));
    };

    if (fastTrackErrorStatuses.includes(data?.status)) {
      setError(createErrorData({ status: 500 }));
      return;
    }

    if (data?.status === fastTrackStatus.COMPLETED) {
      if (data?.tags && flowTag.NO_SAMPLE in data?.tags) {
        setError(
          createErrorData({
            description: data.tags[flowTag.NO_SAMPLE],
            header: 'Insufficient Sample',
            status: 'failed-flow',
          })
        );
      }

      setFlow((prevFlow) => ({
        ...prevFlow,
        status: fastTrackStatus.COMPLETED,
      }));

      if (data?.numberIncompleteTopics?.length > 0) updateIncompleteTopics();
      if (data?.numberCompleteTopics?.length > 0) updateCompletedTopics();
    } else if (data?.completeFlowTopicIds?.length > 0) {
      updateCompletedTopics();
    }
  }, [fastTrackFlow]);

  if (isLoading) {
    return <LoaderView heading={loaderHeader(flow?.status)} messages={loaderMessage(flow)} />;
  }

  const errorStatus = error?.STATUS_ERROR;
  // Do not allow reprompt of i.e. 404
  const isTitleHidden = typeof errorStatus === 'number' && errorStatus >= 400 && errorStatus < 500;
  const title = isTitleHidden ? null : (
    <FastTrackTitle
      downloadData={downloadData}
      flowData={flow}
      handleReprompt={() => setIsModalOpen(true)}
      setFlow={setFlow}
    />
  );

  const body = error ? (
    <Error description={error.ERROR_DESCRIPTION} header={error.ERROR_HEADER} status={errorStatus} />
  ) : (
    <>
      {flow?.topics?.map((topic, index) => (
        <FastTrackTopicCard
          key={topic.id}
          keyIndex={index}
          setDownloadData={setDownloadData}
          topic={topic}
        />
      ))}
      {flow && showLoadingDots && <FastTrackLoadingDots topics={flow.topics} />}
    </>
  );

  return (
    <>
      <PageTitle title={flow?.name || 'Narratives'} />
      {title}
      {body}
      {isModalOpen && (
        <ModalBuildFastTrack
          flowId={flow?.id}
          route={fastTrackRoute}
          setIsModalOpen={setIsModalOpen}
        />
      )}
    </>
  );
};

const FastTrackLayout = () => (
  <Column>
    <FastTrack />
  </Column>
);

export default FastTrackLayout;
